import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Collapse,
  Link,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListSubheader,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import DecimalField from '../shared/DecimalField';
import { formatPriceWithCurrency } from '../../utils/vat';
import { discountSummary, isValidDiscountMonths } from '../../utils/subscription';

const EMPTY_CUSTOMER = {
  company_name: '',
  contact_person: '',
  email: '',
  phone: '',
  address_street: '',
  address_postal_code: '',
  address_city: '',
  address_country: 'FI',
  verkkolasku_address: '',
  verkkolasku_operator: '',
  prefers_verkkolasku: true,
  default_buyers_reference: '',
  preferred_language: 'fi',
};

// Date-only, matching CancelSubscriptionPanel.
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fi-FI') : '-');

// "20" / "12,5": the percent as a person typed it, without trailing zeros.
const formatPercent = (value) => {
  const n = Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? String(n).replace('.', ',') : String(value);
};

export default function StartInvoiceSubscriptionForm({ organizationId, onStarted }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [prefill, setPrefill] = useState(null);

  const [planId, setPlanId] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [delivery, setDelivery] = useState('einvoice');
  const [nextInvoiceDate, setNextInvoiceDate] = useState('');
  const [nextPlanFeeDate, setNextPlanFeeDate] = useState('');
  const [setupFee, setSetupFee] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountMonths, setDiscountMonths] = useState('');
  const [lastInvoicedPeriodEnd, setLastInvoicedPeriodEnd] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    axios
      .get(`/api/vadmin/organizations/${organizationId}/invoice-subscription-prefill/`)
      .then((res) => {
        if (!active) return;
        const d = res.data;
        setPrefill(d);
        // A Stripe client being moved over keeps their current plan by default.
        const defaultPlanId = d.defaults?.plan_id;
        if (defaultPlanId && (d.plans || []).some((p) => p.id === defaultPlanId)) {
          setPlanId(defaultPlanId);
        } else if (d.plans?.length) {
          setPlanId(d.plans[0].id);
        }
        setBillingPeriod(d.defaults?.billing_period || 'monthly');
        setDelivery(d.defaults?.invoice_delivery_type || 'einvoice');
        setNextInvoiceDate(d.defaults?.next_invoice_date || '');
        setNextPlanFeeDate(d.defaults?.next_plan_fee_date || '');
        setCustomer({ ...EMPTY_CUSTOMER, ...(d.customer_prefill || {}) });
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setLoadError(err.response?.data?.detail || err.message);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [organizationId]);

  const setCustomerField = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setCustomer((c) => ({ ...c, [field]: val }));
  };

  const einvoice = delivery === 'einvoice';
  const eligible = prefill?.eligibility?.eligible;

  // A paying Stripe client: the backend cancels Stripe at period end and starts
  // invoicing the day after. The dates are computed from Stripe and locked unless
  // the period end could not be read.
  const migration = prefill?.stripe_migration || null;
  const isMigration = !!eligible && prefill?.eligibility?.mode === 'stripe_migration' && !!migration;
  const migrationDatesLocked = isMigration && !!migration.period_end;
  const periodLabel = (period) =>
    period === 'yearly' ? t('vadmin.yearly') : t('vadmin.monthly');

  // Backend sorts current-generation standard tiers first; older generations,
  // generation-0 plans and test plans are flagged so the picker greys them out.
  const allPlans = prefill?.plans || [];
  const currentPlans = allPlans.filter((p) => p.is_current_generation);
  const legacyPlans = allPlans.filter((p) => !p.is_current_generation);

  const planPrice = (plan) =>
    billingPeriod === 'yearly'
      ? `${formatPriceWithCurrency(plan.price_yearly)} / ${t('vadmin.perYear')}`
      : `${formatPriceWithCurrency(plan.price_monthly)} / ${t('vadmin.perMonth')}`;

  // Discount preview. The backend recomputes everything; this only has to agree with it
  // (payments/services/subscription_pricing.py), which utils/subscription.js mirrors.
  const selectedPlan = allPlans.find((p) => p.id === planId) || null;
  const unitLabel = billingPeriod === 'yearly' ? t('vadmin.perYear') : t('vadmin.perMonth');
  const firstFeeDate = billingPeriod === 'yearly' ? nextPlanFeeDate || nextInvoiceDate : nextInvoiceDate;
  const discountMonthsOk = isValidDiscountMonths(discountMonths, billingPeriod);
  const discountMonthsError =
    discountMonths !== '' && !discountMonthsOk
      ? billingPeriod === 'yearly' && Number.isInteger(Number(discountMonths)) && Number(discountMonths) > 0
        ? t('vadmin.discountMonthsYearlyError')
        : t('vadmin.discountMonthsInvalid')
      : null;
  const hasDiscount = String(discountPercent).trim() !== '' && Number(String(discountPercent).replace(',', '.')) > 0;
  const summary =
    hasDiscount && selectedPlan
      ? discountSummary({
          basePrice: billingPeriod === 'yearly' ? selectedPlan.price_yearly : selectedPlan.price_monthly,
          percent: String(discountPercent).replace(',', '.'),
          months: discountMonths === '' ? null : Number(discountMonths),
          firstFeeDate,
          billingPeriod,
        })
      : null;
  const discountValid = !hasDiscount || (!!summary && discountMonthsOk);

  const canSubmit =
    !submitting &&
    eligible &&
    !!planId &&
    !!nextInvoiceDate &&
    discountValid &&
    (!einvoice ||
      (customer.verkkolasku_address?.trim() && customer.verkkolasku_operator?.trim()));

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const payload = {
        plan_id: planId,
        billing_period: billingPeriod,
        invoice_delivery_type: delivery,
        next_invoice_date: nextInvoiceDate || undefined,
        next_plan_fee_date:
          billingPeriod === 'yearly' ? nextPlanFeeDate || undefined : undefined,
        customer,
      };
      if (hasDiscount) {
        payload.discount_percent = String(discountPercent).replace(',', '.');
        if (discountMonths !== '') payload.discount_months = Number(discountMonths);
      }
      if (isMigration) {
        // Guards against acting on a Stripe subscription other than the one shown.
        payload.expected_stripe_subscription_id = migration.stripe_subscription_id;
      } else {
        payload.setup_fee = setupFee || undefined;
        payload.last_invoiced_period_end = lastInvoicedPeriodEnd || undefined;
      }
      const res = await axios.post(
        `/api/vadmin/organizations/${organizationId}/start-invoice-subscription/`,
        payload
      );
      setSubmitSuccess(res.data.detail || t('vadmin.startSubscriptionSuccess'));
      if (onStarted) await onStarted();
    } catch (err) {
      setSubmitError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const handleSubmitClick = () => {
    if (isMigration) {
      setConfirmOpen(true);
    } else {
      handleSubmit();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  if (!eligible) {
    return (
      <Alert severity="info">
        {prefill?.eligibility?.reason || t('vadmin.notEligibleForInvoiceSub')}
      </Alert>
    );
  }

  if (submitSuccess) {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        {submitSuccess}
      </Alert>
    );
  }

  const sectionTitle = (label) => (
    <Typography
      variant="subtitle2"
      sx={{
        mb: 1.5,
        mt: 1,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: 'text.secondary',
        fontSize: '12px',
      }}
    >
      {label}
    </Typography>
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isMigration ? t('vadmin.migrateStripeIntro') : t('vadmin.startInvoiceSubscriptionIntro')}
      </Typography>

      {isMigration && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="stripe-migration-summary">
          <Typography variant="body2">
            {t('vadmin.migrateStripeCurrent', {
              plan: migration.current_plan_name || '-',
              period: periodLabel(migration.current_billing_period),
              id: migration.stripe_subscription_id,
            })}
          </Typography>
          {migration.period_end ? (
            <>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t('vadmin.migrateStripePeriodEnd', { date: formatDate(migration.period_end) })}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t('vadmin.migrateStripeFirstInvoice', {
                  date: formatDate(nextInvoiceDate || migration.next_invoice_date),
                  fee: billingPeriod === 'yearly' ? t('vadmin.migrateStripeYearlyFee') : t('vadmin.migrateStripeMonthlyFee'),
                  from: formatDate(migration.metered_from),
                })}
              </Typography>
            </>
          ) : null}
          {migration.metered_subscription_id ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('vadmin.migrateStripeMetered', { id: migration.metered_subscription_id })}
            </Typography>
          ) : null}
        </Alert>
      )}

      {isMigration && migration.period_end_source === 'cached' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('vadmin.migrateStripePeriodCached')}
        </Alert>
      )}

      {isMigration && !migration.period_end && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('vadmin.migrateStripePeriodUnknown')}
        </Alert>
      )}

      {!prefill?.billing_org_exists && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('vadmin.billingOrgMissing')}
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {/* Subscription settings */}
      {sectionTitle(t('vadmin.subscriptionSettings'))}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            label={t('vadmin.plan')}
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            SelectProps={{
              renderValue: (value) => {
                const plan = (prefill?.plans || []).find((p) => p.id === value);
                return plan ? `${plan.display_name} — ${planPrice(plan)}` : '';
              },
            }}
          >
            {currentPlans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.display_name} — {planPrice(plan)}
              </MenuItem>
            ))}
            {legacyPlans.length > 0 && <Divider />}
            {legacyPlans.length > 0 && (
              <ListSubheader
                disableSticky
                sx={{ lineHeight: 1.4, py: 1, fontSize: '0.75rem', color: 'text.disabled' }}
              >
                {t('vadmin.legacyPlanWarning')}
              </ListSubheader>
            )}
            {legacyPlans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id} sx={{ color: 'text.disabled' }}>
                <Box>
                  <Box component="span">
                    {plan.display_name} — {planPrice(plan)}
                  </Box>
                  <Typography variant="caption" display="block" sx={{ color: 'text.disabled' }}>
                    {t('vadmin.legacyPlanWarning')}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            label={t('vadmin.billing')}
            value={billingPeriod}
            onChange={(e) => setBillingPeriod(e.target.value)}
          >
            <MenuItem value="monthly">{t('vadmin.monthly')}</MenuItem>
            <MenuItem value="yearly">{t('vadmin.yearly')}</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            label={t('vadmin.deliveryType')}
            value={delivery}
            onChange={(e) => setDelivery(e.target.value)}
          >
            <MenuItem value="einvoice">{t('vadmin.deliveryEinvoice')}</MenuItem>
            <MenuItem value="email">{t('vadmin.deliveryEmail')}</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            type="date"
            fullWidth
            size="small"
            label={t('vadmin.nextInvoiceDate')}
            value={nextInvoiceDate}
            onChange={(e) => setNextInvoiceDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={migrationDatesLocked}
            helperText={migrationDatesLocked ? t('vadmin.migrateStripeDateLocked') : undefined}
          />
        </Grid>
        {billingPeriod === 'yearly' && (
          <Grid item xs={12} sm={6}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label={t('vadmin.nextPlanFeeDate')}
              value={nextPlanFeeDate}
              onChange={(e) => setNextPlanFeeDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={migrationDatesLocked}
              helperText={
                migrationDatesLocked ? t('vadmin.migrateStripeDateLocked') : t('vadmin.nextPlanFeeDateHelp')
              }
            />
          </Grid>
        )}
        {!isMigration && (
          <Grid item xs={12} sm={6}>
            <DecimalField
              fullWidth
              size="small"
              label={t('vadmin.setupFee')}
              value={setupFee}
              onChange={setSetupFee}
              helperText={t('vadmin.setupFeeHelp')}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <DecimalField
            fullWidth
            size="small"
            label={t('vadmin.discountPercent')}
            value={discountPercent}
            onChange={setDiscountPercent}
            helperText={t('vadmin.discountPercentHelp')}
            inputProps={{ 'data-testid': 'discount-percent' }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={t('vadmin.discountMonths')}
            value={discountMonths}
            onChange={(e) => setDiscountMonths(e.target.value)}
            disabled={!hasDiscount}
            error={!!discountMonthsError}
            helperText={discountMonthsError || t('vadmin.discountMonthsHelp')}
            inputProps={{ min: 1, step: 1, 'data-testid': 'discount-months' }}
          />
        </Grid>
      </Grid>

      {summary && (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="discount-summary">
          <Typography variant="body2">
            {t('vadmin.discountSummaryBase', {
              plan: selectedPlan.display_name,
              price: formatPriceWithCurrency(summary.basePrice),
              unit: unitLabel,
            })}
          </Typography>
          <Typography variant="body2">
            {t('vadmin.discountSummaryDiscount', {
              percent: formatPercent(discountPercent),
              amount: formatPriceWithCurrency(summary.discountAmount),
              unit: unitLabel,
            })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {summary.forever
              ? t('vadmin.discountSummaryPriceForever', {
                  price: formatPriceWithCurrency(summary.price),
                  unit: unitLabel,
                })
              : t('vadmin.discountSummaryPriceUntil', {
                  price: formatPriceWithCurrency(summary.price),
                  unit: unitLabel,
                  date: summary.lastDay ? formatDate(summary.lastDay) : t('vadmin.discountSummaryDateUnknown'),
                  count:
                    billingPeriod === 'yearly'
                      ? t('vadmin.discountYearlyFees', { count: summary.periods || 0 })
                      : t('vadmin.discountInvoices', { count: summary.periods || 0 }),
                })}
          </Typography>
          {!summary.forever && (
            <Typography variant="body2">
              {t('vadmin.discountSummaryAfter', {
                price: formatPriceWithCurrency(summary.basePrice),
                unit: unitLabel,
              })}
            </Typography>
          )}
        </Alert>
      )}

      {/* Billing customer */}
      {sectionTitle(t('vadmin.billingCustomerSection'))}
      {einvoice && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {t('vadmin.einvoiceFieldsRequired')}
        </Alert>
      )}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('vadmin.companyName')}
            value={customer.company_name}
            onChange={setCustomerField('company_name')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('vadmin.contactPerson')}
            value={customer.contact_person}
            onChange={setCustomerField('contact_person')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('vadmin.email')}
            value={customer.email}
            onChange={setCustomerField('email')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('Phone')}
            value={customer.phone}
            onChange={setCustomerField('phone')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('vadmin.addressStreet')}
            value={customer.address_street}
            onChange={setCustomerField('address_street')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size="small"
            label={t('vadmin.postalCode')}
            value={customer.address_postal_code}
            onChange={setCustomerField('address_postal_code')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size="small"
            label={t('vadmin.city')}
            value={customer.address_city}
            onChange={setCustomerField('address_city')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('vadmin.einvoiceAddress')}
            value={customer.verkkolasku_address}
            onChange={setCustomerField('verkkolasku_address')}
            required={einvoice}
            error={einvoice && !customer.verkkolasku_address?.trim()}
            helperText={prefill?.ovt_address ? `${t('vadmin.suggestion')}: ${prefill.ovt_address}` : ''}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('vadmin.einvoiceOperator')}
            value={customer.verkkolasku_operator}
            onChange={setCustomerField('verkkolasku_operator')}
            required={einvoice}
            error={einvoice && !customer.verkkolasku_operator?.trim()}
            helperText={t('vadmin.einvoiceOperatorHelp')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={t('vadmin.buyersReference')}
            value={customer.default_buyers_reference}
            onChange={setCustomerField('default_buyers_reference')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LanguageSelect
            fullWidth
            size="small"
            label={t('vadmin.language')}
            value={customer.preferred_language || 'fi'}
            onChange={setCustomerField('preferred_language')}
          />
        </Grid>
      </Grid>

      {/* Advanced (a migration owns its watermark: today) */}
      {!isMigration && (
        <>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setShowAdvanced((v) => !v)}
            sx={{ mb: 1, display: 'inline-block' }}
          >
            {showAdvanced ? t('vadmin.hideAdvanced') : t('vadmin.showAdvanced')}
          </Link>
          <Collapse in={showAdvanced}>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  fullWidth
                  size="small"
                  label={t('vadmin.lastInvoicedPeriodEnd')}
                  value={lastInvoicedPeriodEnd}
                  onChange={(e) => setLastInvoicedPeriodEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={t('vadmin.lastInvoicedPeriodEndHelp')}
                />
              </Grid>
            </Grid>
          </Collapse>
        </>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
        <Button
          variant="contained"
          color={isMigration ? 'warning' : 'primary'}
          onClick={handleSubmitClick}
          disabled={!canSubmit}
          data-testid="start-invoice-subscription-submit"
        >
          {submitting && !confirmOpen ? (
            <CircularProgress size={20} />
          ) : isMigration ? (
            t('vadmin.migrateStripeButton')
          ) : (
            t('vadmin.startSubscriptionButton')
          )}
        </Button>
      </Box>

      {isMigration && (
        <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('vadmin.migrateStripeConfirmTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('vadmin.migrateStripeConfirmBody', {
                id: migration.stripe_subscription_id,
                periodEnd: formatDate(migration.period_end),
                firstInvoice: formatDate(nextInvoiceDate),
              })}
            </Typography>
            <Typography variant="body2">
              {t('vadmin.migrateStripeConfirmPlan', {
                plan: (prefill?.plans || []).find((p) => p.id === planId)?.display_name || '-',
                period: periodLabel(billingPeriod),
                delivery: einvoice ? t('vadmin.deliveryEinvoice') : t('vadmin.deliveryEmail'),
              })}
            </Typography>
            {summary && (
              <Typography variant="body2" sx={{ mt: 1.5 }} data-testid="stripe-migration-confirm-discount">
                {t('vadmin.migrateStripeConfirmDiscount', {
                  percent: formatPercent(discountPercent),
                  price: formatPriceWithCurrency(summary.price),
                  unit: unitLabel,
                  until: summary.forever
                    ? t('vadmin.migrateStripeConfirmDiscountForever')
                    : t('vadmin.migrateStripeConfirmDiscountUntil', {
                        date: summary.lastDay ? formatDate(summary.lastDay) : '-',
                        price: formatPriceWithCurrency(summary.basePrice),
                        unit: unitLabel,
                      }),
                })}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="stripe-migration-confirm"
            >
              {submitting ? <CircularProgress size={20} /> : t('vadmin.migrateStripeConfirmButton')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
